'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Card } from '@/components/ui/card';
import { Activity, Shield, FileText, Zap } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

gsap.registerPlugin(ScrollTrigger);

export default function WhatIsSection() {
    const { t } = useTranslation('root-canal');
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sectionRef.current) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Cards animation
            if (cardsRef.current?.children) {
                const cards = Array.from(cardsRef.current.children);
                gsap.fromTo(cards,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.15,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    const aspects = [
        {
            icon: <Activity className="w-8 h-8 text-gold" />,
            titleKey: 'whatIs.aspects.endodontic.title',
            descriptionKey: 'whatIs.aspects.endodontic.description',
            highlightKey: 'whatIs.aspects.endodontic.highlight'
        },
        {
            icon: <Shield className="w-8 h-8 text-gold" />,
            titleKey: 'whatIs.aspects.painRelief.title',
            descriptionKey: 'whatIs.aspects.painRelief.description',
            highlightKey: 'whatIs.aspects.painRelief.highlight'
        },
        {
            icon: <FileText className="w-8 h-8 text-gold" />,
            titleKey: 'whatIs.aspects.preservation.title',
            descriptionKey: 'whatIs.aspects.preservation.description',
            highlightKey: 'whatIs.aspects.preservation.highlight'
        },
        {
            icon: <Zap className="w-8 h-8 text-gold" />,
            titleKey: 'whatIs.aspects.technology.title',
            descriptionKey: 'whatIs.aspects.technology.description',
            highlightKey: 'whatIs.aspects.technology.highlight'
        }
    ];

    return (
        <section id="what-is" ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <div className="container">
                <div
                    ref={titleRef}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif mb-6">
                        {t('whatIs.title.part1')} <span className="text-gold">{t('whatIs.title.part2')}</span> {t('whatIs.title.part3')}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        {t('whatIs.subtitle')}
                    </p>
                </div>

                <div ref={cardsRef} className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {aspects.map((aspect, index) => (
                        <Card
                            key={index}
                            className="group relative p-8 border-2 border-transparent bg-white dark:bg-slate-800 hover:border-gold hover:bg-gold/5 transition-all duration-300 hover:shadow-xl hover:shadow-gold/20 overflow-hidden cursor-pointer transform hover:scale-105 hover:-translate-y-2"
                        >
                            {/* Background gradient overlay on hover */}
                            <div className="absolute inset-0 opacity-0 group-hover:bg-gold/10 group-hover:opacity-100 transition-all duration-300"></div>

                            {/* Decorative corner element */}
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gold/10 opacity-0 group-hover:opacity-100 transition-all duration-300 transform rotate-45 translate-x-10 -translate-y-10 group-hover:translate-x-6 group-hover:-translate-y-6"></div>

                            <div className="relative z-10 flex flex-col items-center text-center">
                                {/* Icon with rotation animation */}
                                <div className="mb-6 transform group-hover:rotate-12 transition-transform duration-300">
                                    <div className="w-16 h-16 bg-gold/10 rounded-xl flex items-center justify-center group-hover:bg-gold/20 transition-all duration-300">
                                        {aspect.icon}
                                    </div>
                                </div>

                                {/* Highlight badge */}
                                <div className="mb-4 px-3 py-1 bg-gold/10 text-gold text-xs font-semibold rounded-full group-hover:bg-gold/20 transition-colors duration-300">
                                    {t(aspect.highlightKey)}
                                </div>

                                <h3 className="text-xl font-bold text-navy dark:text-white mb-4 group-hover:text-gold transition-colors duration-300">
                                    {t(aspect.titleKey)}
                                </h3>

                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed group-hover:text-slate-700 dark:group-hover:text-white transition-colors duration-300">
                                    {t(aspect.descriptionKey)}
                                </p>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    );
} 